import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';

const FORM_ACTION = 'https://readdy.ai/api/form/dbb5tsmctmbk4sp5cbt0';

const principles = [
  {
    icon: 'ri-layout-grid-line',
    title: '分类先行',
    desc: '六大题材维度 + 地区、视角、年份三重筛选，把「找到想看的内容」的路径压缩到三次点击以内。',
  },
  {
    icon: 'ri-file-text-line',
    title: '信息完整',
    desc: '每个条目都具备标题、封面、时长、年份、地区与简介，列表与详情页的信息保持一致，不会出现空壳页面。',
  },
  {
    icon: 'ri-eye-off-line',
    title: '演示边界清晰',
    desc: '播放区域始终标注为演示组件，不展示播放成功的假象，也不提供下载入口或真实播放权限。',
  },
];

const legalItems = [
  '本站仅面向已年满 18 周岁、且所在国家或地区允许浏览成人向内容的成年人开放。',
  '站内全部视频标题、封面、简介、时长与观看数据均为虚构示范数据，不对应任何真实作品、真实人物或真实事件。',
  '播放区域为前端界面演示组件，不接入任何真实视频接口，不具备播放、缓存或下载功能。',
  '本站不提供账号注册、在线支付与内容交易，收藏状态仅保存在浏览器本地，不会上传至服务器。',
  '如你所在地区禁止浏览此类内容，请立即离开本站；继续浏览即视为你已自行确认合规性并承担相应责任。',
];

export default function AboutPage() {
  usePageMeta({
    title: '关于我们 - 无码中文字幕',
    description:
      '了解无码中文字幕的定位、内容范围与编辑原则，查看成人内容合规声明与免责声明，并通过反馈表单与我们联系。',
    keywords: '无码中文字幕,关于我们,日韩自拍视频,免责声明,成人内容规范',
  });

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [count, setCount] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get('website_alt') || '').trim();

    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      setCount(0);
      return;
    }

    formData.delete('website_alt');

    const message = String(formData.get('message') || '').trim();
    if (!message) {
      setStatus('error');
      setFormError('请先填写反馈内容再提交。');
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string' && value.trim() !== '') {
        payload.append(key, value);
      }
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ACTION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const succeeded = response.ok && parsed?.code === 'OK';

      if (!succeeded) {
        setStatus('error');
        setFormError(
          serverMessage && !serverMessage.toLowerCase().includes('spam')
            ? serverMessage
            : '提交未成功，请稍后重试，或直接通过邮件联系我们。',
        );
        return;
      }

      setStatus('success');
      setFormError('');
      form.reset();
      setCount(0);
    } catch {
      setStatus('error');
      setFormError('网络请求失败，请检查网络后重试，或直接通过邮件联系我们。');
    }
  };

  return (
    <div className="w-full">
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-14">
          <nav className="flex flex-wrap items-center gap-1.5 text-[11px] text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="transition-colors hover:text-primary-700">
              首页
            </Link>
            <span className="flex h-3 w-3 items-center justify-center text-background-400">
              <i className="ri-arrow-right-s-line text-xs" />
            </span>
            <span className="text-foreground-800">关于我们</span>
          </nav>

          <p className="mt-6 flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-accent-700">
            <span className="inline-block h-[6px] w-[6px] rounded-full bg-accent-500" />
            About · 无码中文字幕
          </p>
          <h1 className="mt-3 max-w-[720px] font-heading text-2xl font-semibold leading-9 text-foreground-950 md:text-[34px] md:leading-[1.35]">
            把日韩自拍内容的分类型浏览，做成一件清晰又轻的事
          </h1>
          <p className="mt-4 max-w-[680px] text-[13px] leading-7 text-foreground-600">
            无码中文字幕是一个面向成年观众的影视内容浏览示范站。我们不做内容堆砌，而是把精力放在分类维度、
            筛选逻辑与页面信息密度上：让每一次浏览都能快速定位，让每一个条目都有完整、可信的元信息。
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <div className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-4 py-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-50 text-primary-700">
                <i className="ri-shield-check-line text-sm" />
              </span>
              <div>
                <p className="text-[12px] font-medium text-foreground-900">18+ 内容提示</p>
                <p className="text-[10px] text-foreground-500">进入前需确认年龄</p>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-4 py-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-50 text-accent-700">
                <i className="ri-database-2-line text-sm" />
              </span>
              <div>
                <p className="text-[12px] font-medium text-foreground-900">虚构示范数据</p>
                <p className="text-[10px] text-foreground-500">不对应真实作品</p>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-50 px-4 py-2.5">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-secondary-100 text-secondary-900">
                <i className="ri-movie-2-line text-sm" />
              </span>
              <div>
                <p className="text-[12px] font-medium text-foreground-900">演示播放器</p>
                <p className="text-[10px] text-foreground-500">不接入真实接口</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.15fr] lg:gap-14">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-foreground-400">Our scope</p>
            <h2 className="mt-2 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">我们做什么</h2>
            <p className="mt-4 text-[13px] leading-7 text-foreground-600">
              内容范围聚焦日韩两地的自拍类影视内容，按题材整理为情侣自拍、业余风、校园风、素人系列、温泉旅拍与
              女优合集六个方向，并在每个分类下提供地区、视角与年份的叠加筛选。
            </p>
            <p className="mt-3 text-[13px] leading-7 text-foreground-600">
              站内不设排行榜营销与诱导性文案，页面以封面、标题、时长与标签为基本信息单元，尽量降低浏览时的视觉负担。
              关于详情页，我们只保留必要元信息、简短简介、播放演示与相关推荐四块内容。
            </p>

            <div className="mt-6 overflow-hidden rounded-lg border border-background-200 bg-primary-950">
              <div className="relative h-[200px] w-full md:h-[230px]">
                <img
                  src="https://readdy.ai/api/search-image?query=cinematic%20still%20life%20of%20vintage%20film%20reels%20and%20a%20dim%20editing%20desk%20lamp%20in%20a%20dark%20studio%20with%20warm%20amber%20highlights%20and%20deep%20navy%20shadows%20soft%20film%20grain%20editorial%20photography%20no%20people&width=900&height=500&seq=wmzm-about-01&orientation=landscape"
                  alt="影视内容编辑与分类整理的氛围图"
                  title="无码中文字幕内容编辑流程示意"
                  className="h-full w-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-primary-950/10 to-transparent" />
                <p className="absolute bottom-3 left-4 text-[11px] text-background-200">
                  分类整理 · 元信息校对 · 推荐关联
                </p>
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="text-[13px] font-medium text-foreground-900">内容编辑流程</h3>
              <ol className="mt-3 flex flex-col gap-3">
                {['按题材归类并统一命名规则', '补齐时长、年份、地区与视角元信息', '关联同分类 / 同标签推荐', '定期复查失效与重复条目'].map(
                  (step, index) => (
                    <li key={step} className="flex items-start gap-2.5">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-600 text-[10px] font-medium text-background-50">
                        {index + 1}
                      </span>
                      <span className="text-[12px] leading-5 text-foreground-600">{step}</span>
                    </li>
                  ),
                )}
              </ol>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1">
            {principles.map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-700">
                  <i className={`${item.icon} text-base`} />
                </span>
                <div>
                  <h3 className="text-[14px] font-medium text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-[12px] leading-6 text-foreground-500">{item.desc}</p>
                </div>
              </div>
            ))}
            <div className="flex items-start gap-3 rounded-lg border border-accent-200 bg-accent-50 p-4 md:p-5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-500 text-primary-950">
                <i className="ri-alert-line text-base" />
              </span>
              <div>
                <h3 className="text-[14px] font-medium text-accent-900">内容均为虚构</h3>
                <p className="mt-1.5 text-[12px] leading-6 text-accent-900/80">
                  站内不存在真实拍摄资源、真实人物资料或可播放下载地址。请勿将本站内容作为任何形式的真实信息引用。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="legal" className="w-full border-y border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.3fr] lg:gap-14">
            <div>
              <p className="text-[11px] uppercase tracking-[0.22em] text-foreground-400">Legal notice</p>
              <h2 className="mt-2 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">法律声明与免责声明</h2>
              <p className="mt-4 text-[13px] leading-7 text-foreground-600">
                本站为影视内容站的界面与交互示范项目，用于展示分类浏览、筛选、搜索与详情页信息结构的实现方式。
                所有页面均不构成对任何真实内容的推荐、销售或分发。
              </p>
              <div className="mt-5 flex items-start gap-2.5 rounded-lg border border-background-300 bg-background-50 p-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-accent-500 bg-accent-50 font-heading text-[11px] font-semibold text-accent-700">
                  18+
                </span>
                <p className="text-[12px] leading-6 text-foreground-600">
                  未成年人请勿浏览本站。若你未满 18 周岁，或所在地区不允许浏览成人向内容，请立即关闭本页面。
                </p>
              </div>
            </div>

            <ul className="flex flex-col gap-3">
              {legalItems.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-2.5 rounded-lg border border-background-200 bg-background-50 p-4"
                >
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-primary-600">
                    <i className="ri-checkbox-circle-line text-sm" />
                  </span>
                  <span className="text-[12px] leading-6 text-foreground-600">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section id="feedback" className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.2fr] lg:gap-14">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-foreground-400">Contact</p>
            <h2 className="mt-2 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">联系与反馈</h2>
            <p className="mt-4 max-w-[420px] text-[13px] leading-7 text-foreground-600">
              如果你发现条目信息有误、分类归属不当，或希望提出功能建议，可以通过下方表单留言，
              也可以直接发送邮件。我们通常在 2 个工作日内查看。
            </p>

            <a
              href="mailto:hello@wumazhongwen.example"
              className="mt-5 flex max-w-[360px] items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3 transition-colors hover:border-primary-300"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50 text-primary-700">
                <i className="ri-mail-send-line text-base" />
              </span>
              <span>
                <span className="block text-[11px] text-foreground-500">邮箱</span>
                <span className="block text-[13px] text-foreground-900">hello@wumazhongwen.example</span>
              </span>
            </a>

            <div id="contact" className="mt-5 rounded-lg border border-background-200 bg-background-50 p-4">
              <h3 className="text-[13px] font-medium text-foreground-900">反馈处理说明</h3>
              <p className="mt-2 text-[12px] leading-6 text-foreground-500">
                表单收集的信息仅用于回复你的反馈，不会用于营销推送。请勿在留言中填写个人敏感信息。
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-7">
            <h3 className="font-heading text-lg font-semibold text-foreground-950">在线留言</h3>
            <p className="mt-1.5 text-[12px] text-foreground-500">带 * 的字段为必填项，留言最多 500 字。</p>

            <form
              id="feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-6 flex flex-col gap-4"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="min-w-0">
                  <label htmlFor="fb-name" className="mb-1.5 block text-[12px] text-foreground-600">
                    称呼 *
                  </label>
                  <input
                    id="fb-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="h-11 w-full box-border rounded-md border border-background-300 bg-background-50 px-3 text-[13px] text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
                <div className="min-w-0">
                  <label htmlFor="fb-email" className="mb-1.5 block text-[12px] text-foreground-600">
                    邮箱 *
                  </label>
                  <input
                    id="fb-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="h-11 w-full box-border rounded-md border border-background-300 bg-background-50 px-3 text-[13px] text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
              </div>

              <div className="min-w-0">
                <label htmlFor="fb-topic" className="mb-1.5 block text-[12px] text-foreground-600">
                  反馈类型
                </label>
                <select
                  id="fb-topic"
                  name="topic"
                  defaultValue="内容信息纠错"
                  className="h-11 w-full box-border cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-[13px] text-foreground-900 outline-none transition-colors focus:border-primary-400"
                >
                  <option value="内容信息纠错">内容信息纠错</option>
                  <option value="分类与筛选建议">分类与筛选建议</option>
                  <option value="页面体验问题">页面体验问题</option>
                  <option value="版权与合规问题">版权与合规问题</option>
                  <option value="其他">其他</option>
                </select>
              </div>

              <div className="min-w-0">
                <label htmlFor="fb-message" className="mb-1.5 block text-[12px] text-foreground-600">
                  留言内容 *
                </label>
                <textarea
                  id="fb-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请描述你遇到的问题或建议…"
                  onChange={(event) => setCount(event.target.value.length)}
                  className="w-full box-border resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-[13px] leading-6 text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
                <div className="mt-1 flex justify-between text-[11px] text-foreground-400">
                  <span>请勿填写个人敏感信息</span>
                  <span>{count} / 500</span>
                </div>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />

              {status === 'error' && formError && (
                <div className="flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-3 py-2.5">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-accent-700">
                    <i className="ri-error-warning-line text-sm" />
                  </span>
                  <p className="text-[12px] leading-5 text-accent-900">{formError}</p>
                </div>
              )}

              {status === 'success' && (
                <div className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-primary-700">
                    <i className="ri-checkbox-circle-line text-sm" />
                  </span>
                  <p className="text-[12px] leading-5 text-primary-800">
                    反馈已提交成功，感谢你的建议。我们会尽快查看并通过邮件回复。
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-[11px] leading-5 text-foreground-400">
                  提交即表示你已阅读并同意本站的
                  <Link to="/about#legal" className="mx-0.5 text-foreground-600 underline decoration-background-300 underline-offset-2">
                    使用条款
                  </Link>
                  。
                </p>
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className={`flex h-11 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-6 text-[13px] transition-colors ${
                    status === 'submitting'
                      ? 'cursor-wait bg-background-300 text-foreground-500'
                      : 'bg-primary-600 text-background-50 hover:bg-primary-700'
                  }`}
                >
                  {status === 'submitting' ? (
                    <>
                      <span className="flex h-4 w-4 animate-spin items-center justify-center">
                        <i className="ri-loader-4-line text-base" />
                      </span>
                      提交中…
                    </>
                  ) : (
                    <>
                      <span className="flex h-4 w-4 items-center justify-center">
                        <i className="ri-send-plane-line text-sm" />
                      </span>
                      提交反馈
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}